import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Eye,
  Clock,
  CheckCircle,
  Circle,
  CircleDot,
  XCircle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  RotateCcw,
  SkipForward,
  Ban,
  MessageSquare,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useWorkflowRunDetail, useWorkflowRunStream, useCancelWorkflowRun, useRetryWorkflowRun, useSkipNode, useApprovePhase, useRejectPhase, useAuth, useNowTick, useSecurityReviewApprovalBlock } from '@/hooks'
import { cn } from '@/lib/utils'
import { extractValidationMessage } from '@/lib/validationErrors'
import { getErrorMessage } from '@/api/errors'
import { formatElapsedTime } from '@/lib/timeFormatters'
import { WorkflowRunStatuses, WorkflowPhaseStatuses, isActiveRunStatus, isCancellableRunStatus, getPhaseEffectiveStatus } from '@/types'
import { canManageWorkflowRun, WorkflowStatusBadge } from '../shared'
import { RejectPhaseModal } from '../runs/RejectPhaseModal'
import { Permissions } from '@/lib/permissions'
import type { WorkflowPhaseStatus } from '@/types'
import { toast } from 'sonner'

interface WorkflowRunCardProps {
  runId: string
  projectId: string
}

const PHASE_ICON: Record<WorkflowPhaseStatus, React.ComponentType<{ className?: string }>> = {
  [WorkflowPhaseStatuses.Completed]: CheckCircle,
  [WorkflowPhaseStatuses.Running]: CircleDot,
  [WorkflowPhaseStatuses.Failed]: XCircle,
  [WorkflowPhaseStatuses.Pending]: Circle,
  [WorkflowPhaseStatuses.Cancelled]: XCircle,
  [WorkflowPhaseStatuses.Skipped]: Circle,
  [WorkflowPhaseStatuses.AwaitingApproval]: AlertCircle,
  [WorkflowPhaseStatuses.AwaitingUserInput]: MessageSquare,
}


export function WorkflowRunCard({ runId, projectId }: WorkflowRunCardProps) {
  const { projectId: routeProjectId } = useParams()
  const navigate = useNavigate()
  const effectiveProjectId = projectId || routeProjectId
  const { hasPermission, user, isAdmin } = useAuth(effectiveProjectId)
  const [collapsed, setCollapsed] = useState(false)
  const [streamReconnectKey, setStreamReconnectKey] = useState(0)
  const [rejectPhaseInfo, setRejectPhaseInfo] = useState<{ phaseId: string; phaseName: string } | null>(null)
  const cancelRun = useCancelWorkflowRun(projectId)
  const retryRun = useRetryWorkflowRun(projectId)
  const approvePhase = useApprovePhase(projectId)
  const rejectPhase = useRejectPhase(projectId)
  const { data: runDetail, isLoading, isFetchedAfterMount } = useWorkflowRunDetail(projectId, runId)
  const failedNode = runDetail?.phases
    ?.flatMap(p => p.branches)
    ?.flatMap(b => b.nodes)
    ?.find(n => n.status === 'Failed')
  const skipNode = useSkipNode(projectId, runId)
  useWorkflowRunStream(projectId, runId, isFetchedAfterMount && !!runDetail && isActiveRunStatus(runDetail.status), streamReconnectKey)
  const run = runDetail
  useNowTick(!!run && isActiveRunStatus(run.status))

  const canApprove = hasPermission(Permissions.Project.Workflow.Approve, effectiveProjectId)
  const manageAllowed = canManageWorkflowRun({ triggeredByUserId: run?.triggeredByUserId ?? null }, user?.id, isAdmin)
  const cancelAllowed = hasPermission(Permissions.Project.Workflow.Cancel, effectiveProjectId) && manageAllowed
  const retryAllowed = hasPermission(Permissions.Project.Workflow.Trigger, effectiveProjectId) && manageAllowed
  const skipAllowed = hasPermission(Permissions.Project.Workflow.Intervene, effectiveProjectId) && manageAllowed
  const pendingApprovalPhaseId = run?.pendingApprovalPhaseId ?? run?.approvalRequests?.find(a => a.status === 'Pending')?.phaseId
  const securityReviewBlock = useSecurityReviewApprovalBlock(effectiveProjectId ?? '', run?.status === WorkflowRunStatuses.AwaitingApproval ? runId : null)

  if (isLoading) {
    return (
      <div className="border rounded-lg p-3 bg-card" data-testid="workflow-run-card-loading" role="status" aria-live="polite">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex gap-2 mt-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-24" />
        </div>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="border rounded-lg p-3 bg-muted/30 text-sm text-muted-foreground" data-testid="workflow-run-card-empty">
        Workflow run not found.
      </div>
    )
  }

  const effectiveRunStatus = run.phases.some(p => getPhaseEffectiveStatus(p) === 'AwaitingUserInput')
    ? 'AwaitingUserInput' as const
    : run.status

  if (collapsed) {
    return (
      <button
        className="w-full border rounded-lg px-3 py-2 bg-muted/30 flex items-center gap-2 text-sm hover:bg-muted/50 transition-colors text-left"
        onClick={() => setCollapsed(false)}
        data-testid="workflow-run-card-collapsed"
      >
        <WorkflowStatusBadge status={effectiveRunStatus} className="text-[10px] py-0 px-2" />
        <span className="font-medium truncate">{run.definitionDisplayName}</span>
        <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatElapsedTime(run.startedAt, run.completedAt)}
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
      </button>
    )
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-card" data-testid="workflow-run-card">
      <div className="px-3 py-2 border-b bg-muted/30 flex items-center gap-2">
        <WorkflowStatusBadge status={effectiveRunStatus} className="text-[10px] py-0 px-2" />
        <span className="font-medium text-sm truncate">{run.definitionDisplayName}</span>
        <span className="text-[10px] px-2 py-0.5 rounded bg-muted text-muted-foreground">v{run.definitionVersion}</span>
        <button
          className="ml-auto text-muted-foreground hover:text-foreground"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse card"
        >
          <ChevronUp className="h-3 w-3" />
        </button>
      </div>

      <div className="px-3 py-2">
        <div className="flex items-center gap-1 overflow-x-auto">
          {run.phases.map((phase, i) => {
            const effectiveStatus = getPhaseEffectiveStatus(phase)
            const Icon = PHASE_ICON[effectiveStatus] ?? Circle
            return (
              <div key={phase.name} className="flex items-center gap-1">
                {i > 0 && <span className="text-muted-foreground text-xs">&rarr;</span>}
                <div
                  className={cn(
                    'flex items-center gap-1 px-2 py-0.5 rounded text-xs',
                    effectiveStatus === WorkflowPhaseStatuses.Completed && 'text-success-foreground bg-success',
                    effectiveStatus === WorkflowPhaseStatuses.Running && 'text-info-foreground bg-info',
                    effectiveStatus === WorkflowPhaseStatuses.Failed && 'text-danger-foreground bg-danger',
                    (effectiveStatus === WorkflowPhaseStatuses.AwaitingApproval || effectiveStatus === WorkflowPhaseStatuses.AwaitingUserInput) && 'text-warning-foreground bg-warning',
                    effectiveStatus === WorkflowPhaseStatuses.Pending && 'text-muted-foreground',
                    effectiveStatus === WorkflowPhaseStatuses.Skipped && 'text-muted-foreground line-through',
                  )}
                >
                  <Icon className={cn('h-3 w-3', effectiveStatus === WorkflowPhaseStatuses.Running && 'animate-pulse')} />
                  <span className="whitespace-nowrap">{phase.name}</span>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatElapsedTime(run.startedAt, run.completedAt)}
          </span>
          <span>by {run.triggeredBy}</span>
          {(run.status === WorkflowRunStatuses.Running || effectiveRunStatus === 'AwaitingUserInput') && run.currentPhaseName && (
            <span className="text-info-foreground">&rarr; {run.currentPhaseName}</span>
          )}
        </div>

        {run.status === WorkflowRunStatuses.Failed && run.errorSummary && (
          <p className="mt-2 text-xs text-danger-foreground bg-danger p-2 rounded" role="alert">
            {run.errorSummary}
          </p>
        )}
      </div>

      <div className="px-3 py-2 border-t bg-muted/20 flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => navigate(`/projects/${effectiveProjectId}/workflows/runs/${run.id}`)}
        >
          <Eye className="h-3 w-3 mr-1" />
          View Run
        </Button>
        {isCancellableRunStatus(run.status) && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={cancelRun.isPending || !cancelAllowed}
            title={cancelAllowed ? undefined : 'Only an administrator or the user who started the workflow can cancel it.'}
            onClick={() => cancelRun.mutate(run.id)}
          >
            <Ban className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        )}
        {(run.status === WorkflowRunStatuses.Cancelled || run.status === WorkflowRunStatuses.Failed) && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={retryRun.isPending || !retryAllowed}
            title={retryAllowed
              ? 'Resume from where execution stopped — completed phases are preserved'
              : 'Only an administrator or the user who started the workflow can retry it.'}
            onClick={() => retryRun.mutate(run.id, { onSuccess: () => setStreamReconnectKey(k => k + 1) })}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
        {run.status === WorkflowRunStatuses.AwaitingApproval && canApprove && pendingApprovalPhaseId && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-success-foreground"
              disabled={approvePhase.isPending || securityReviewBlock.disabled}
              title={securityReviewBlock.reason}
              onClick={() =>
                approvePhase.mutate(
                  { phaseId: pendingApprovalPhaseId },
                  {
                    onSuccess: () => {
                      toast.success('Phase approved')
                      setStreamReconnectKey(k => k + 1)
                    },
                    onError: (error) => toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to approve phase'),
                  },
                )
              }
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-danger-foreground"
              disabled={rejectPhase.isPending}
              onClick={() =>
                setRejectPhaseInfo({
                  phaseId: pendingApprovalPhaseId,
                  phaseName: run.currentPhaseName ?? 'Phase',
                })
              }
            >
              <XCircle className="h-3 w-3 mr-1" />
              Reject
            </Button>
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          disabled={!failedNode || skipNode.isPending || !skipAllowed}
          title={skipAllowed ? undefined : 'Only an administrator or the user who started the workflow can skip its nodes.'}
          onClick={() => failedNode && skipNode.mutate(failedNode.id, {
            onSuccess: () => setStreamReconnectKey(k => k + 1),
            onError: (err) => toast.error(extractValidationMessage(err) ?? 'Failed to skip node'),
          })}
        >
          <SkipForward className="h-3 w-3 mr-1" />
          Skip
        </Button>
      </div>
      <RejectPhaseModal
        phaseName={rejectPhaseInfo?.phaseName ?? ''}
        open={!!rejectPhaseInfo}
        onOpenChange={(open) => {
          if (!open) setRejectPhaseInfo(null)
        }}
        onConfirm={(reason) => {
          if (!rejectPhaseInfo) return
          rejectPhase.mutate(
            { phaseId: rejectPhaseInfo.phaseId, reason },
            {
              onSuccess: () => {
                toast.success('Phase rejected')
                setRejectPhaseInfo(null)
                setStreamReconnectKey(k => k + 1)
              },
              onError: () => toast.error('Failed to reject phase'),
            },
          )
        }}
        isPending={rejectPhase.isPending}
      />
    </div>
  )
}
